import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { MasterCodeItem } from '@/shared/services/lookups';
import { warnDuplicateKeys } from '@/shared/lib/warn-duplicate-keys';

/**
 * GET /api/Master/PatientImportance — patient-priority lookup (Board Member,
 * VIP, etc.). Endpoint lives on the Master service but the concept is
 * patient-specific, so the hook stays in patients/services.
 *
 * Ships the FHIR `Code` shape `{ code, display, definition }` — NOT
 * `{ id, name }` (curl-verified 2026-06-04). Consumers key/value by `code`
 * and label by `display`; binding `.id` / `.name` yields blank options and
 * duplicate empty-string React keys.
 */
export function usePatientImportance() {
  return useQuery({
    queryKey: ['lookup', 'patient-importance'],
    queryFn: async ({ signal }) => {
      const items = await get<MasterCodeItem[]>('/api/Master/PatientImportance', {
        signal,
      });
      return warnDuplicateKeys(items ?? [], (i) => i.code, 'patient-importance');
    },
    staleTime: 60 * 60 * 1000,
  });
}
